import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Trophy, RefreshCw, Zap, Timer, Target } from 'lucide-react';
import playerManager from '../../utils/playerManager';

const TapFlash = ({ onGameComplete, gameConfig }) => {
  const [gameState, setGameState] = useState('waiting'); // waiting, ready, active, finished
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [totalRounds] = useState(10);
  const [reactionTime, setReactionTime] = useState(null);
  const [bestReactionTime, setBestReactionTime] = useState(null);
  const [averageReactionTime, setAverageReactionTime] = useState(null);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [showTarget, setShowTarget] = useState(false);
  const [targetColor, setTargetColor] = useState('#00FF00');
  const [message, setMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState(gameConfig?.duration || 60);
  const [startTime, setStartTime] = useState(null);
  const [reactionStartTime, setReactionStartTime] = useState(null);

  const colors = ['#00FF00', '#FFD700', '#FF4444', '#4444FF', '#FF00FF', '#00FFFF'];
  const timeoutRef = useRef(null);
  const gameTimerRef = useRef(null);

  // Game timer
  useEffect(() => {
    if (gameState === 'ready' || gameState === 'active') {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      gameTimerRef.current = timer;
      return () => clearInterval(timer);
    }
  }, [gameState]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (gameTimerRef.current) clearInterval(gameTimerRef.current);
    };
  }, []);

  const startGame = () => {
    setGameState('ready');
    setScore(0);
    setRound(0);
    setReactionTimes([]);
    setBestReactionTime(null);
    setAverageReactionTime(null);
    setTimeLeft(gameConfig?.duration || 60);
    setStartTime(Date.now());
    setMessage('Get ready! Tap the screen when it flashes!');
    
    // Start first round after a short delay
    setTimeout(() => {
      nextRound();
    }, 1500);
  };

  const nextRound = () => {
    if (round >= totalRounds) {
      endGame();
      return;
    }

    setGameState('ready');
    setShowTarget(false);
    setReactionTime(null);
    setMessage('Wait for the flash...');
    
    // Random delay between 1-4 seconds
    const delay = Math.random() * 3000 + 1000;
    
    timeoutRef.current = setTimeout(() => {
      showFlash();
    }, delay);
  };

  const showFlash = () => {
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    setTargetColor(randomColor);
    setShowTarget(true);
    setGameState('active');
    setReactionStartTime(Date.now());
    setMessage('TAP NOW!');
  };

  const handleTap = () => {
    if (gameState === 'ready') {
      // Tapped too early
      setMessage('❌ Too early! Wait for the flash!');
      setScore(prev => Math.max(0, prev - 50)); // Penalty for early tap
      setTimeout(() => {
        nextRound();
      }, 1000);
      return;
    }

    if (gameState === 'active' && showTarget) {
      const reactionMs = Date.now() - reactionStartTime;
      setReactionTime(reactionMs);
      
      // Calculate score based on reaction time
      // Faster = more points (max 200 for < 200ms, down to 50 for > 1000ms)
      let points = 0;
      if (reactionMs < 200) points = 200;
      else if (reactionMs < 300) points = 150;
      else if (reactionMs < 400) points = 120;
      else if (reactionMs < 500) points = 100;
      else if (reactionMs < 700) points = 75;
      else points = 50;

      setScore(prev => prev + points);
      setReactionTimes(prev => [...prev, reactionMs]);
      
      // Update best reaction time
      if (!bestReactionTime || reactionMs < bestReactionTime) {
        setBestReactionTime(reactionMs);
      }

      // Calculate average
      const newReactionTimes = [...reactionTimes, reactionMs];
      const avg = newReactionTimes.reduce((a, b) => a + b, 0) / newReactionTimes.length;
      setAverageReactionTime(avg);

      setMessage(`⚡ ${reactionMs}ms! +${points} points`);
      setShowTarget(false);
      setRound(prev => prev + 1);

      // Move to next round
      setTimeout(() => {
        if (round + 1 >= totalRounds) {
          endGame();
        } else {
          nextRound();
        }
      }, 1200);
    }
  };

  const endGame = async () => {
    setGameState('finished');
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (gameTimerRef.current) clearInterval(gameTimerRef.current);

    const finalScore = Math.round(score);
    const timeTaken = Date.now() - startTime;

    const gameData = {
      total_rounds: totalRounds,
      completed_rounds: round,
      best_reaction_time: bestReactionTime,
      average_reaction_time: averageReactionTime,
      reaction_times: reactionTimes,
      time_taken: timeTaken
    };

    try {
      await playerManager.submitScore('tap-flash', 'Tap Flash', finalScore, gameData);
      
      if (onGameComplete) {
        onGameComplete('tap-flash', finalScore, gameData);
      }
    } catch (error) {
      console.error('Error submitting score:', error);
    }
  };

  const handleRestart = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (gameTimerRef.current) clearInterval(gameTimerRef.current);
    setGameState('waiting');
    setScore(0);
    setRound(0);
    setReactionTimes([]);
    setBestReactionTime(null);
    setAverageReactionTime(null);
    setShowTarget(false);
    setMessage('');
  };

  return (
    <div className="w-full h-full flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl gaming-card">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl text-white flex items-center justify-center gap-3">
            <Zap className="w-8 h-8 text-yellow-400" />
            Tap Flash
            <Zap className="w-8 h-8 text-yellow-400" />
          </CardTitle>
          <p className="text-gray-400 mt-2">Fastest finger wins! Tap as soon as you see the flash!</p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Game Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-gray-400 text-sm">Score</div>
              <div className="text-2xl font-bold text-yellow-400">{Math.round(score)}</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-gray-400 text-sm">Round</div>
              <div className="text-2xl font-bold text-white">{round}/{totalRounds}</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-gray-400 text-sm flex items-center justify-center gap-1">
                <Timer className="w-4 h-4" />
                Time
              </div>
              <div className="text-2xl font-bold text-blue-400">{timeLeft}s</div>
            </div>
          </div>

          {/* Game Area */}
          {gameState === 'waiting' ? (
            <div className="text-center py-12">
              <div className="mb-6">
                <Target className="w-20 h-20 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-3">Ready to Test Your Reflexes?</h3>
                <p className="text-gray-400 mb-2">How to Play:</p>
                <ul className="text-gray-300 text-sm space-y-1 text-left max-w-md mx-auto">
                  <li>• Wait for the screen to flash with color</li>
                  <li>• Tap/click as fast as possible when you see it</li>
                  <li>• Faster reactions = more points!</li>
                  <li>• Don't tap too early or you'll lose points</li>
                  <li>• Complete 10 rounds</li>
                </ul>
              </div>
              <Button
                onClick={startGame}
                className="btn-primary text-lg px-8 py-6"
              >
                <Zap className="w-5 h-5 mr-2" />
                Start Game
              </Button>
            </div>
          ) : gameState === 'finished' ? (
            <div className="text-center py-8">
              <Trophy className="w-20 h-20 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-3xl font-bold text-white mb-4">Game Complete!</h3>
              
              <div className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 rounded-lg p-6 mb-6">
                <div className="text-5xl font-bold text-yellow-400 mb-2">{Math.round(score)}</div>
                <div className="text-gray-400">Total Points</div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-gray-400 text-sm mb-1">Best Reaction</div>
                  <div className="text-xl font-bold text-green-400">{bestReactionTime}ms</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-gray-400 text-sm mb-1">Average Reaction</div>
                  <div className="text-xl font-bold text-blue-400">{Math.round(averageReactionTime)}ms</div>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handleRestart}
                  className="btn-primary w-full"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Play Again
                </Button>
              </div>
            </div>
          ) : (
            <div>
              {/* Active Game Area */}
              <div
                onClick={handleTap}
                className={`relative rounded-lg transition-all duration-100 cursor-pointer ${
                  showTarget
                    ? 'shadow-2xl shadow-yellow-500/50'
                    : ''
                }`}
                style={{
                  backgroundColor: showTarget ? targetColor : '#1a1a2e',
                  height: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: showTarget ? `4px solid ${targetColor}` : '4px solid #2a2a3e'
                }}
              >
                <div className="text-center">
                  <div className={`text-3xl font-bold mb-4 ${showTarget ? 'text-black' : 'text-white'}`}>
                    {message}
                  </div>
                  {showTarget && (
                    <div className="animate-pulse">
                      <Zap className="w-24 h-24 text-black mx-auto" />
                    </div>
                  )}
                  {!showTarget && gameState === 'ready' && (
                    <div className="text-gray-500 text-lg">
                      Waiting...
                    </div>
                  )}
                </div>
              </div>

              {/* Reaction Time Display */}
              {reactionTime && (
                <div className="text-center mt-4">
                  <div className="inline-block bg-gradient-to-r from-yellow-600 to-orange-600 text-black font-bold px-6 py-3 rounded-lg">
                    ⚡ {reactionTime}ms reaction time!
                  </div>
                </div>
              )}

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span>Progress</span>
                  <span>{round}/{totalRounds} rounds</span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(round / totalRounds) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TapFlash;
